import { useState } from 'react';

import { downloadBlob } from '../../../../../../lib/downloadBlob';
import { dashboardT } from '../../../../../../lib/messages';
import { showRequestError } from '../../../../../../lib/requestErrorToast';
import type { TargetingCsvControl } from '../../../../../targeting/components/TargetingForm/TargetingForm.types';
import type { createCampaignsClient } from '../../../api';
import {
  LineItemTargetingCsvKind,
  lineItemTargetingCsvFilename,
  readLineItemTargetingCsvCount,
} from '../../../lineItemTargetingCsvModel';
import type { LineItemTargetingCsvFailure } from '../../../saveLineItemWithTargetingCsv';
import { LineItemTargetingCsvNotice } from './LineItemTargetingCsvNotice';

const csvKindLabelKeys = {
  [LineItemTargetingCsvKind.ZipCodes]: 'targeting.lineItemCsv.zipCodes',
  [LineItemTargetingCsvKind.IpAddresses]: 'targeting.lineItemCsv.ipAddresses',
} as const;

/** Builds the ZIP/IP CSV section controls; files stay pending until the line item is saved. */
export function useLineItemTargetingCsv({
  client,
  failures,
  files,
  lineItemId,
  onFileChange,
  savedTargeting,
}: {
  client: Pick<ReturnType<typeof createCampaignsClient>, 'exportLineItemTargetingCsv'>;
  failures: Partial<Record<LineItemTargetingCsvKind, LineItemTargetingCsvFailure>>;
  files: Readonly<Record<LineItemTargetingCsvKind, File | null>>;
  lineItemId?: string | undefined;
  onFileChange: (kind: LineItemTargetingCsvKind, file: File | null) => void;
  savedTargeting: Readonly<Record<string, unknown>> | null | undefined;
}): Record<LineItemTargetingCsvKind, TargetingCsvControl> {
  const [exporting, setExporting] = useState<ReadonlySet<LineItemTargetingCsvKind>>(new Set());

  function setKindExporting(kind: LineItemTargetingCsvKind, active: boolean) {
    setExporting((current) => {
      const next = new Set(current);
      if (active) next.add(kind);
      else next.delete(kind);
      return next;
    });
  }

  function control(kind: LineItemTargetingCsvKind): TargetingCsvControl {
    const count = readLineItemTargetingCsvCount(savedTargeting, kind);
    const file = files[kind];
    // A failure belongs to the file that produced it; choosing another file clears the notice.
    const failure = failures[kind]?.file === file ? failures[kind] : undefined;
    return {
      targetingId: lineItemId ?? '',
      count,
      exporting: exporting.has(kind),
      file,
      helperText: dashboardT('targeting.lineItemCsv.help'),
      // No element without a file: an empty notice still adds a grid row and gap to the section.
      notice: file ? (
        <LineItemTargetingCsvNotice
          collection={dashboardT(csvKindLabelKeys[kind])}
          count={count}
          failure={failure}
        />
      ) : undefined,
      onFileChange: (nextFile) => {
        // The saved count stays authoritative until the import succeeds.
        onFileChange(kind, nextFile);
      },
      onExport: () => {
        if (!lineItemId || exporting.has(kind)) return;
        setKindExporting(kind, true);
        void client
          .exportLineItemTargetingCsv(lineItemId, kind)
          .then((blob) => {
            downloadBlob(blob, lineItemTargetingCsvFilename(lineItemId, kind));
          })
          .catch((error: unknown) => {
            showRequestError(error, dashboardT('integrations.targetingCsvExportError'));
          })
          .finally(() => {
            setKindExporting(kind, false);
          });
      },
    };
  }

  return {
    [LineItemTargetingCsvKind.ZipCodes]: control(LineItemTargetingCsvKind.ZipCodes),
    [LineItemTargetingCsvKind.IpAddresses]: control(LineItemTargetingCsvKind.IpAddresses),
  };
}
